import Papa from "papaparse"

/**
 * Parses and validates a CSV recipient file against the expected process structure.
 * Uses PapaParse internally and stores the parsed rows in `csvData` after a successful
 * call to {@link RecipientFileParser#processFile}.
 */
export default class RecipientFileParser {
  /**
   * @param {File}   file - Browser `File` object to parse.
   * @param {string} [delimiter=";"] - Column delimiter character.
   * @param {boolean} [skipEmptyLines=true] - Whether to skip blank rows during parsing.
   * @param {object} [extraConfig={}] - Additional PapaParse config options (merged last, lowest priority).
   */
  constructor (file, delimiter=";", skipEmptyLines=true, extraConfig={}) {
    this.file = file
    this.delimiter = delimiter
    this.skipEmptyLines = skipEmptyLines
    this.extraConfig = extraConfig

    /** @type {object[]|null} Parsed CSV rows; populated after a successful `processFile` call. */
    this.csvData = null
  }

  /**
   * Transforms a raw PapaParse result into a normalised response object.
   * @param {object} results - PapaParse `complete` callback argument.
   * @returns {{ error: true, errors: object[] } | { headers: string[], data: object[] }}
   */
  __parsingCompletedCallback(results) {
    if (results.errors.length > 0) {
      return { error: true, errors: results.errors }
    }

    return { headers: results.meta.fields, data: results.data }
  }

  /**
   * Checks that the CSV column headers exactly match the process-defined headers (order-sensitive).
   * @param {string[]|null} fileHeaders - Headers detected from the file.
   * @param {string[]|null} processHeaders - Headers required by the process definition.
   * @returns {boolean|null} `true` if they match, `false` if they differ, `null` if either is falsy.
   */
  __validateHeadersWithProcessHeaders(fileHeaders, processHeaders) {
    if (fileHeaders && processHeaders) {
      return fileHeaders.toString() === processHeaders.toString()
    }

    return null
  }

  /**
   * Partitions a process's field definitions into single-file and multi-file field name lists.
   * @param {object} processFields - Map of field name → field definition (must include a `type` property).
   * @returns {{ fileFields: string[], arrayFileFields: string[] }}
   */
  __extractFileFields(processFields) {
    return {
      fileFields: Object.keys(processFields).filter(k => "file" === processFields[k].type),
      arrayFileFields: Object.keys(processFields).filter(k => "array_file" === processFields[k].type)
    }
  }

  /**
   * Collects every referenced filename from file and array-file columns across all CSV rows.
   * Requires `processFile` to have been called first (i.e., `this.csvData` must be populated).
   * @param {object} processFields - Map of field name → field definition.
   * @returns {Set<string>|undefined} Deduplicated set of filenames, or `undefined` if `csvData` is null.
   * @throws {Error} When a single-file field contains a comma-separated list of filenames.
   */
  extractExtraResourcesFilenames(processFields) {
    if (this.csvData) {
      const {fileFields, arrayFileFields} = this.__extractFileFields(processFields)

      const allFilenames = this.csvData
        .map(row => {
          const fileFilenames = fileFields.map(x => row[x])
          const arrayFileFilenames = arrayFileFields.map(x => row[x].split(",")).flat()

          if (fileFilenames.some(x => x.includes(","))) {
            const fieldNames = fileFields.filter(x => row[x].includes(","))
            const error = new Error("File fields cannot contain multiple filenames separated by commas")
            error.options = { fieldNames }
            throw error
          }

          return [...fileFilenames, ...arrayFileFilenames]
        })
        .flat()
        .filter(x => x && x != "")

      return new Set(allFilenames)
    }
  }

  /**
   * Validates that the `contact_id` column is non-empty for every row.
   * @returns {{ error: boolean, message: Array<{row: number, errors: string[]}>|null }|undefined}
   */
  __validateContactId() {
    if (this.csvData) {
      const result = this.csvData.map((row, index) => {
        const errors = row["contact_id"] === "" ? ["contact_id"] : []

        return { row: index + 1, errors }
      })

      const rowsWithErrors = result.filter(x => x.errors.length > 0)

      if (rowsWithErrors.length > 0) {
        return { error: true, message: rowsWithErrors }
      }

      return { error: false, message: null }
    }

  }

  /**
   * Validates that every file-type field contains a filename with an allowed extension.
   * Fields without an `allowed_extensions` validation rule are skipped.
   * @param {object} processFields - Map of field name → field definition.
   * @returns {{ error: boolean, message: Array|null }|undefined}
   */
  __validateFileExtensions(processFields) {
    if (this.csvData) {
      const {fileFields, arrayFileFields} = this.__extractFileFields(processFields)
      const fileFieldsWithExtensionValidation = [...fileFields, ...arrayFileFields]
        .filter(x => {
          return processFields[x].validations?.allowed_extensions
        })
        .map(x => ({
          fieldName: x,
          extensions: processFields[x].validations?.allowed_extensions.map(x => x.toLowerCase())
        }))

        const result = this.csvData.map((row, index) => {
          const errors = fileFieldsWithExtensionValidation.filter((field) => {
            const filenames = row[field.fieldName].split(",")
            const extensions = filenames.map(x => x.toLowerCase().split("."))

            return extensions.some(x => x[0] == "" || x[1] !== "" && !field.extensions.includes(x[1]))
          })

          return { row: index + 1, errors }
        })

      const rowsWithErrors = result.filter(x => x.errors.length > 0)

      if (rowsWithErrors.length > 0) {
        return { error: true, message: rowsWithErrors }
      }

      return { error: false, message: null }
    }
  }

  /**
   * Parses the file and runs all validations in sequence.
   * On success, `this.csvData` is populated with the parsed rows.
   * @param {string[]} processHeaders - Expected column headers in order.
   * @param {object}  processFields   - Map of field name → field definition used for validation.
   * @param {string}  [encoding="utf-8"] - File encoding passed to PapaParse.
   * @returns {Promise<{
   *   error: boolean,
   *   message?: string,
   *   detail?: object,
   *   headers?: string[],
   *   data?: object[]
   * }>} Resolves to an error descriptor or the parsed file payload.
   */
  async processFile(processHeaders, processFields, encoding="utf-8") {
    const res = await new Promise((resolve) => {
      Papa.parse(this.file, {
        header: true,
        encoding,
        skipEmptyLines: this.skipEmptyLines,
        config: { delimiter: this.delimiter, ...this.extraConfig },
        complete: (results) => {
          resolve(this.__parsingCompletedCallback(results))
        }
      })
    })

    if (res.error) {
      // throw new Error('Malformed file')
      return { error: true, message: "Malformed file" }
    }

    if (!this.__validateHeadersWithProcessHeaders(res.headers, processHeaders)) {
      // throw new Error('File headers and process headers are different')
      return { error: true, message: "File headers and process headers are different" }
    }

    this.csvData = res.data
    const contactFieldsValidationResult = this.__validateContactId()

    if (contactFieldsValidationResult.error) {
      return { error: true, message: "Contact field empty", detail: contactFieldsValidationResult.message }
    }

    const extensionValidationResult = this.__validateFileExtensions(processFields)
    if (extensionValidationResult.error) {
      return { error: true, message: "File extension not allowed", detail: extensionValidationResult.message }
    }

    return { error: false, ...res }
  }
}